// === Custom breakpoints (px) ===
// Source of truth for Slim v2's custom breakpoints.
// `BREAKPOINT_TABLET` is consumed by `tailwind.config.ts` to register the
// `slim2-tablet:` responsive variant — that's why class strings below can
// use `slim2-tablet:` as a regular Tailwind prefix. Change this value and
// every `slim2-tablet:` class in the codebase resolves to the new pixel
// width automatically.
export const BREAKPOINT_TABLET = 906; // overrides Tailwind's `md` (768)
export const BREAKPOINT_DESKTOP = 1280; // matches Tailwind's `xl`

// === Size-tiered styles ===
// Classes grouped by size tier. They compose additively (mobile → tablet →
// desktop) — each tier only contains the variants that override the
// previous tier.
export type SlimV2SizeStyles = {
  container: string;
  inner: string;
  title: string;
  description: string;
  ctaRow: string;
  cta: string;
  ctaSlot: string;
  endDateRow: string;
  imageBox: string;
  imageFill: string;
};

type Tier = "mobile" | "tablet" | "desktop";

export const STYLES: Record<Tier, SlimV2SizeStyles> = {
  // < BREAKPOINT_TABLET
  mobile: {
    container: "h-auto py-6 px-4",
    inner: "flex-col gap-6",
    title: "text-[28px]",
    description: "text-[18px] mb-6",
    ctaRow: "items-start gap-4",
    cta: "w-full h-10 text-[14px]",
    ctaSlot: "h-10 w-full",
    endDateRow: "h-10 w-full",
    imageBox: "h-[179px] sm:h-[300px] w-full",
    imageFill: "object-top",
  },
  // ≥ BREAKPOINT_TABLET — see `slim2-tablet` screen in tailwind.config.ts
  tablet: {
    container:
      "slim2-tablet:h-[304px] slim2-tablet:py-[44px] slim2-tablet:px-8",
    inner: "slim2-tablet:flex-row slim2-tablet:items-center slim2-tablet:gap-9",
    title: "slim2-tablet:text-[36px]",
    description: "slim2-tablet:text-[14px]",
    ctaRow:
      "slim2-tablet:flex-row slim2-tablet:items-center slim2-tablet:gap-6",
    cta: "slim2-tablet:w-auto slim2-tablet:h-9",
    ctaSlot: "slim2-tablet:h-9 slim2-tablet:w-auto",
    endDateRow: "slim2-tablet:h-9 slim2-tablet:w-auto",
    imageBox: "slim2-tablet:h-full slim2-tablet:flex-1",
    imageFill: "slim2-tablet:object-center",
  },
  // ≥ BREAKPOINT_DESKTOP (Tailwind `xl`)
  desktop: {
    container: "xl:h-[365px] xl:py-14 xl:px-12",
    inner: "xl:gap-10",
    title: "xl:text-[48px]",
    description: "xl:text-[20px] xl:mb-7",
    ctaRow: "",
    cta: "xl:h-[41px] xl:text-[18px]",
    ctaSlot: "xl:h-[41px]",
    endDateRow: "xl:h-[41px]",
    imageBox: "xl:h-[253px] xl:w-[396px] xl:flex-none",
    imageFill: "",
  },
};

// Compose all size tiers for a given element key.
export const cx = (key: keyof SlimV2SizeStyles) =>
  [STYLES.mobile[key], STYLES.tablet[key], STYLES.desktop[key]]
    .filter(Boolean)
    .join(" ");

// === Reusable CTA classes for upstream callers ===
// These mirror Slim.v2's `ctaSlot` wrapper sizing so any external
// `ctaElement` content (e.g. enroll buttons) lines up with the wrapper.
// Used by ProgramPromoBanner and OCPromoBanner.
export const SLIM_V2_CTA_HEIGHT = "h-10 slim2-tablet:h-9 xl:h-[41px]";
export const SLIM_V2_CTA_WIDTH = "w-full slim2-tablet:w-auto";

// Slim v2's Side Rail rendering pivots layout at the same custom
// `slim2-tablet` breakpoint (906px) as the standard Slim v2 — keeping
// both Slim v2 layouts in lock-step. These mirror the inner CTA slot
// sizing in `Slim.v2.SideRail.tsx`.
//
// `lg:h-[42px]` / `xl:h-[42px]` are intentional explicit overrides:
// upstream callers (e.g. `ProgramEnrollButton`'s `baseStyles` includes
// `lg:h-auto`) inject competing height utilities at higher breakpoints,
// which would otherwise collapse the button to its text height.
export const SLIM_V2_SIDERAIL_CTA_HEIGHT =
  "h-10 slim2-tablet:h-[42px] lg:h-[42px] xl:h-[42px]";
export const SLIM_V2_SIDERAIL_CTA_WIDTH = "w-full slim2-tablet:w-auto";
